import { Navigate } from "react-router-dom";
import { MyComponentProps } from "./auth";
import { getRefreshToken } from "./getRefreshToken";
import { useNavigate } from "react-router-dom";
import { handleRedirect } from "../pages/Login";
import { authStateManager } from "./authStateManager";
import { useEffect, useState } from "react";
import { logger } from "../../utils/logger";

export const RequireAuth = ({
  children,
  loadingShell,
}: {
  children: MyComponentProps;
  loadingShell?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);

  // Debug functions available globally for console testing (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      (window as any).forceTokenExpiration = () => {
        console.log("🧪 DEBUG: Forcing token expiration for testing");
        authStateManager.clearAuthState();
        localStorage.removeItem("loggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("token_secret");
        window.location.reload();
      };

      (window as any).simulateExpiredToken = () => {
        console.log(
          "🧪 DEBUG: Simulating expired token (keeping tokens but marking as invalid)"
        );
        localStorage.removeItem("loggedIn");
        window.location.reload();
      };
    }
  }, []);

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        // Quick check using cookies only
        const authState = authStateManager.getAuthState();

        if (authState.isAuthenticated) {
          // Render immediately if we have valid authentication
          setShouldRender(true);
          setIsChecking(false);
          return;
        }

        // If we have no tokens at all, redirect immediately
        if (!authState.hasValidToken && !authState.hasValidRefreshToken) {
          logger.warn(
            "No valid tokens found, redirecting to Cognito immediately"
          );
          // Store only the path portion for security and proper handling
          const currentPath =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          localStorage.setItem("redirectURL", currentPath);
          handleRedirect();
          return;
        }

        // If we have a refresh token but no access token, try to refresh
        if (authState.hasValidRefreshToken && !authState.hasValidToken) {
          logger.info("Attempting background token refresh...");
          try {
            await new Promise<void>((resolve, reject) => {
              getRefreshToken(navigate, () => {
                // Check state again after refresh attempt
                const newState = authStateManager.getAuthState();
                if (newState.isAuthenticated && newState.hasValidToken) {
                  setShouldRender(true);
                  setIsChecking(false);
                  resolve();
                } else {
                  reject(new Error("Token refresh failed"));
                }
              });
            });
            return;
          } catch (error) {
            logger.error("Token refresh failed:", error);
            // If refresh fails, clear auth state and redirect to Cognito
            authStateManager.clearAuthState();
            // Store only the path portion for security and proper handling
            const currentPath =
              window.location.pathname +
              window.location.search +
              window.location.hash;
            localStorage.setItem("redirectURL", currentPath);
            handleRedirect();
            return;
          }
        }

        // If we reach here, authentication failed
        logger.warn("Authentication failed, redirecting to Cognito");
        // Store only the path portion for security and proper handling
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        localStorage.setItem("redirectURL", currentPath);
        handleRedirect();
      } catch (error) {
        logger.error("Error checking authentication state:", error);
        // Store only the path portion for security and proper handling
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        localStorage.setItem("redirectURL", currentPath);
        handleRedirect();
      } finally {
        setIsChecking(false);
      }
    };

    checkAuthState();
  }, [navigate]);

  // Show loading state while checking authentication
  if (isChecking) {
    if (loadingShell) {
      return loadingShell;
    }
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="lds-facebook font-black mb-4">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Render children if authenticated
  if (shouldRender) {
    return children;
  }

  // This should not be reached due to the redirect in useEffect
  // Redirect to Cognito instead of /login page
  return null; // Will redirect to Cognito via handleRedirect()
};
